"use client";

import { useEffect, useState } from "react";
import { Menu, Search, X, Globe, Check } from "lucide-react";
import { CartIcon } from "@/components/shop/CartIcon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
const languages = [{
  code: "en",
  name: "English",
  flag: "",
  dir: "ltr"
}, {
  code: "ar",
  name: "العربية",
  flag: "",
  dir: "rtl"
}, {
  code: "fa",
  name: "فارسی",
  flag: "",
  dir: "rtl"
}];
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        <div className="h-4 w-16 bg-secondary animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-secondary animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-secondary animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-secondary animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative text-foreground hover:text-primary hover:bg-transparent focus:bg-transparent px-3 py-2 text-xs font-default font-medium uppercase tracking-[0.15em] transition-colors group bg-transparent">
                <span>{item.label}</span>
                <span className="absolute left-3 right-3 -bottom-0.5 h-px bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 mt-12">
        <div className="h-10 w-48 bg-secondary animate-pulse rounded-md" />
        <div className="h-10 w-48 bg-secondary animate-pulse rounded-md" />
        <div className="h-10 w-48 bg-secondary animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="relative flex flex-col gap-8 mt-12 pl-6">
      <span className="absolute left-0 top-2 bottom-2 w-px bg-primary/30" aria-hidden="true" />
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onClose} className="font-default text-3xl text-foreground hover:text-primary transition-colors duration-300">
          {item.label}
        </Link>)}
    </nav>;
}
export default function IndustrialLeadershipHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState("en");
  const [langOpen, setLangOpen] = useState(false);
  useEffect(() => {
    const savedLang = localStorage.getItem("akyasco-lang") || "en";
    setCurrentLang(savedLang);
    const langData = languages.find(l => l.code === savedLang);
    if (langData) {
      document.documentElement.dir = langData.dir;
      document.documentElement.lang = langData.code;
    }
  }, []);
  const handleLanguageChange = (code: string) => {
    setCurrentLang(code);
    localStorage.setItem("akyasco-lang", code);
    const langData = languages.find(l => l.code === code);
    if (langData) {
      document.documentElement.dir = langData.dir;
      document.documentElement.lang = langData.code;
    }
    setLangOpen(false);
    window.location.reload();
  };
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="2448" id="industrial-leadership-header" className={`sticky top-0 z-50 w-full bg-background text-foreground transition-all duration-[400ms] ease-out ${scrolled ? "bg-background/90 backdrop-blur-sm border-b border-primary/20" : "bg-background border-b border-border"}`}>
      <div className={`container mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between transition-all duration-[400ms] ease-out ${scrolled ? "h-16" : "h-20"}`}>
        <div className="flex items-center flex-shrink-0 gap-4">
          <Link to="/" aria-label="Akyasco home" className="flex items-center">
            <WvcLogo className="h-7 w-auto" />
          </Link>

          {}
          <div className="hidden sm:block relative">
            <Button variant="ghost" size="sm" onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-muted-foreground hover:text-primary">
              <Globe className="size-3.5" />
              <span>{currentLang}</span>
            </Button>
            
            {langOpen && <>
                <div className="fixed inset-0 z-40" onClick={() => setLangOpen(false)} />
                <div className="absolute top-full left-0 mt-2 w-32 bg-card border border-border shadow-xl z-50 animate-in fade-in zoom-in-95 duration-200">
                  {languages.map((lang, i) => <Button key={lang.code} data-index={i} variant="ghost" size="sm" onClick={() => handleLanguageChange(lang.code)} className="w-full flex items-center justify-between px-3 py-2 text-xs font-mono hover:bg-accent hover:text-primary transition-colors text-left h-auto">
                      <span className="flex items-center gap-2">
                        <span>{lang.flag}</span>
                        <span>{lang.name}</span>
                      </span>
                      {currentLang === lang.code && <Check className="size-3 text-primary" />}
                    </Button>)}
                </div>
              </>}
          </div>
        </div>

        <div className="hidden lg:flex items-center justify-center flex-1">
          <MenuProvider menu_id="22">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        <div className="flex items-center gap-1 sm:gap-2">
          <div className="hidden lg:flex items-center">
            {searchOpen ? <div className="relative flex items-center animate-in fade-in slide-in-from-right-2 duration-300">
                <Input type="text" autoFocus placeholder="Search products..." className="w-56 h-9 bg-transparent border-0 border-b border-primary/40 px-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:border-primary" />
                <Button variant="ghost" size="icon" onClick={() => setSearchOpen(false)} aria-label="Close search" className="h-9 w-9 text-foreground hover:bg-transparent hover:text-primary">
                  <X className="size-4" />
                </Button>
              </div> : <Button variant="ghost" size="icon" onClick={() => setSearchOpen(true)} aria-label="Open search" className="text-foreground hover:bg-transparent hover:text-primary">
                <Search className="size-[18px]" strokeWidth={1.5} />
              </Button>}
          </div>

          <CartIcon variant="ghost" size="icon" iconSize="size-[18px]" className="text-foreground hover:bg-transparent hover:text-primary" />

          <Button variant="ghost" size="icon" onClick={() => setMobileOpen(true)} aria-label="Open menu" className="lg:hidden text-foreground hover:bg-transparent hover:text-primary">
            <Menu className="size-5" strokeWidth={1.5} />
          </Button>
        </div>
      </div>

      {mobileOpen && <div className="lg:hidden fixed inset-0 z-[100] bg-background/95 backdrop-blur-xl animate-in fade-in duration-300" role="dialog" aria-modal="true">
          <div className="flex items-center justify-between h-20 px-4 sm:px-6 border-b border-border">
            <Link to="/" aria-label="Akyasco home" onClick={() => setMobileOpen(false)}>
              <WvcLogo className="h-7 w-auto" />
            </Link>
            <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)} aria-label="Close menu" className="text-foreground hover:bg-transparent hover:text-primary">
              <X className="size-5" strokeWidth={1.5} />
            </Button>
          </div>

          <div className="px-6 sm:px-8 py-8 overflow-y-auto h-[calc(100vh-5rem)]">
            <div className="relative flex items-center mb-8">
              <Search className="absolute left-0 size-4 text-muted-foreground" strokeWidth={1.5} />
              <Input type="text" placeholder="Search products" className="w-full h-10 bg-transparent border-0 border-b border-primary/40 pl-7 pr-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:border-primary" />
            </div>

            {}
            <div className="mb-8 border-b border-border pb-6">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-4">[SELECT] Language</p>
              <div className="flex flex-wrap gap-2">
                {languages.map((lang, i) => <Button key={lang.code} data-index={i} variant={currentLang === lang.code ? "default" : "outline"} size="sm" onClick={() => handleLanguageChange(lang.code)} className="text-xs font-mono">
                    {lang.flag} {lang.name}
                  </Button>)}
              </div>
            </div>

            <MenuProvider menu_id="22">
              <MobileMenuConsumer onClose={() => setMobileOpen(false)} />
            </MenuProvider>
          </div>
        </div>}
    </section>;
}